import React, { useState } from 'react';
import { Alert, Button, Form, Modal } from 'react-bootstrap';
import i18n from 'i18n';
import _, { isNil } from 'lodash';
import SimpleLoader from 'components/loaders/SimpleLoader';
import BzmIcon from 'components/icons/BzmIcon';
import { showToastNotification } from 'store/ui/toasts/toastsActions';
import { ToastType } from 'store/ui/toasts/toastsTypes';
import { getErrorMessage } from 'utils/miscUtil';
import { useDispatch } from 'react-redux';
import PasswordInput from 'components/forms/PasswordInput/PasswordInput';
import InlineHelp from 'components/ui/inlineHelp/InlineHelpProps';
import {
  downloadAsset,
  fetchCertificatesThunk,
  updateMetadata,
  validate,
} from 'store/certificates/certificatesActions';
import { AliasMeta, Certificate, PrimaryMetadata } from 'types/rest/assetCatalogTypes';
import { isAssetKeystoreAliasError, isAssetKeystorePasswordError, isKeystoreFile } from 'utils/assetUtils';
import AliasesTable from './AliasesTable';
import acApi from '../../../axios/ac/acApi';
import { SUPPORTED_CERT_EXTENSIONS, SUPPORTED_KEYSTORES } from '../../../constants/bzm-constants';
import UploadInput from '../../../components/uploadInput/UploadInput';
import BlazeTableActions from '../../../components/blazeTable/BlazeTableActions';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import { copyToClipboard } from '../../../components/buttons/CopyToClipboardButton';

export const isAliasesListFullGranted = (al: AliasImport[]): boolean =>
  al.length === 0 || _.filter(al, a => a.accessGranted === false).length === 0;

export const hasPassword = (pass: any): boolean => {
  return pass !== null && pass !== undefined && pass.trim() !== '';
};

export const isAliasesListFullGrantedFromMeta = (aliases: any): boolean => {
  if (aliases === null || aliases === undefined) {
    return false;
  }
  return _.every(aliases, elements => elements['access_granted'] !== false);
};

export const convertAliasMetaToAliasImport = (aliases: any): AliasImport[] => {
  return _.map(
    aliases,
    (elements, key) =>
      ({
        name: key,
        password: hasPassword(elements['password']) ? elements['password'] : null,
        type: elements['type'],
        accessGranted: elements['access_granted'],
      } as AliasImport),
  );
};

export const convertAliasImportToAliasMetaList = (aliases: AliasImport[]) => {
  const aliasesDetails = {};
  _.forEach(aliases, alias => {
    aliasesDetails[alias.name] = {
      password: hasPassword(alias.password) ? alias.password : null,
      type: alias.type,
      access_granted: alias.accessGranted,
    } as AliasMeta;
  });
  return aliasesDetails;
};

interface EditCertModalProps {
  /** When modal is closed. */
  onClose: () => void;

  /** Certificate file name. */
  fileName?: string;

  /** Certificate for edition */
  cert?: Certificate;

  /** Action after validation Alias. */
  onValidateAliases?: (cert: Certificate) => void;
}

export interface AliasImport {
  isValid?: boolean;
  name: string;
  password: string;
  type?: any;
  accessGranted?: boolean;
}

const CertificateDetailsModal = (props: EditCertModalProps) => {
  const isEditCert = props.fileName !== undefined;
  const [file, setFile] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);

  const resolvePasswords = () => {
    let pass = {
      keyStore: '',
      ksGranted: false,
    };
    if (isEditCert) {
      pass = {
        keyStore: hasPassword(props.cert?.primaryMetadata?.keystore_password)
          ? atob(props.cert?.primaryMetadata?.keystore_password)
          : '',
        ksGranted: props.cert?.primaryMetadata?.keystore_access_granted
          ? props.cert?.primaryMetadata?.keystore_access_granted
          : false,
      };
    }
    return pass;
  };
  //Passwords for the keystore
  const [passwords, setPasswords] = useState(resolvePasswords());
  const [isUpdatingPasswords, setIsUpdatingPasswords] = useState(false);

  const resolveAliases = (): AliasImport[] => {
    let aliases = [] as AliasImport[];
    if (isEditCert && props.cert?.primaryMetadata?.aliases) {
      aliases = convertAliasMetaToAliasImport(props.cert.primaryMetadata.aliases);
      //isValid is used to keep the Alias status that came from the response instead of the one that is under edition
      aliases.forEach(a => {
        a.isValid = !_.isNil(a.password) ? a.accessGranted : true;
      });
    }
    return aliases;
  };

  //List of aliases and passwords (if keystore needs any)
  const [aliases, setAliases] = useState<AliasImport[]>(resolveAliases());

  // Indicates JKS passwords validity
  const [passwordValidationMsg, setPasswordValidationMsg] = useState(null);

  // ID of the uploaded asset
  const [assetId, setAssetId] = useState(isEditCert ? props.cert?.id : null);
  const [certAsset, setCertAsset] = useState(props.cert ? props.cert : null);
  const dispatch = useDispatch();

  const clearFileInput = () => {
    setFile(null);
  };

  const closeModal = () => {
    // Clean up & finish
    setFile(null);
    setCertAsset(null);
    props.onClose();
  };

  const isCertificatedWithValidData = (): boolean => {
    return (!isKeyStore && certAsset !== null) || (passwords.ksGranted === true && isAliasesListFullGranted(aliases));
  };

  const getCertificateContent = async (certId: number) => {
    const response = (await acApi.assets.getById(certId)).data.result;
    if (!isNil(response)) {
      setCertAsset(response);
    }
    dispatch(fetchCertificatesThunk(true));
  };

  /**
   * Save passwords to the backend and validate the asset
   * @param assetId
   */
  const saveAndValidateKeystorePasswords = async (certId: number) => {
    if (isCertificatedWithValidData()) {
      closeModal();
      return;
    }

    setIsUpdatingPasswords(true);
    try {
      let passwordMetadata: PrimaryMetadata = {
        keystore_password: btoa(passwords.keyStore),
        keystore_access_granted: passwords.ksGranted,
      };

      const aliasesDetails = convertAliasImportToAliasMetaList(aliases);
      passwordMetadata = { ...passwordMetadata, aliases: aliasesDetails };
      await updateMetadata({ id: certId, primaryMetadata: passwordMetadata } as Certificate);
      const validateResult = await validate(certId, true);
      const certValidated: Certificate = validateResult.data.result;
      const passResult = {
        keyStore: atob(certValidated.primaryMetadata.keystore_password),
        ksGranted: certValidated.primaryMetadata.keystore_access_granted,
      };
      setPasswords(passResult);
      if (!_.isNil(passwordValidationMsg)) {
        setPasswordValidationMsg(null);
      }

      //Maps list from backend to aliases field
      const resultAlias = convertAliasMetaToAliasImport(certValidated.primaryMetadata.aliases);
      //isValid is used to keep the Alias status that came from the response instead of the one that is under edition
      resultAlias.forEach(a => {
        a.isValid = !_.isNil(a.password) ? a.accessGranted : true;
      });
      setAliases(resultAlias);
      props.onValidateAliases(certValidated);
    } catch (e) {
      const errorMsg = getErrorMessage(e);
      if (isAssetKeystorePasswordError(errorMsg)) {
        setPasswordValidationMsg(`${errorMsg} Please provide a valid keystore password and continue.`);
        setPasswords({ keyStore: passwords.keyStore, ksGranted: false });
        setAliases([]);
      } else if (isAssetKeystoreAliasError(errorMsg)) {
        setPasswords({ keyStore: passwords.keyStore, ksGranted: true });
        setPasswordValidationMsg(`${errorMsg} Password for alias is not correct.`);
      }
    } finally {
      setIsUpdatingPasswords(false);
    }
  };

  /**
   * Patches asset password fields in primary metadata.
   */
  const onUpdatePasswordHandler = async () => {
    try {
      await saveAndValidateKeystorePasswords(assetId);
    } catch (e) {
      processCertificateErrors(e);
    }
  };

  const onSaveHandler = async () => {
    setIsUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    formData.append('type', 'CERTIFICATE');

    let certId;
    try {
      //Upload File
      const resp = await acApi.assets.upload(formData);
      certId = _.get(resp, 'data.result.data.assetId');

      //Save asset id for future validation calls
      setAssetId(certId);

      //Keystore file
      if (isKeystoreFile(file.name)) {
        await saveAndValidateKeystorePasswords(certId);
      } else {
        await getCertificateContent(certId);
      }
    } catch (e) {
      processCertificateErrors(e);
    } finally {
      setIsUploading(false);
    }
  };

  /**
   * Processes errors from backend.
   * @param e
   */
  const processCertificateErrors = (e: any) => {
    const errorMsg = _.get(e, 'response.data.error');
    if (errorMsg === 'uploaded file is not a Certificate') {
      setErrorMessage(i18n.t('certificates:errorMsgs.uploadedFileIsNotCert'));
    } else {
      dispatch(showToastNotification(ToastType.Danger, 'Cannot Upload Certificate', getErrorMessage(e)));
    }
  };

  const getBulletPoints = () => {
    const bullets = [];
    for (let i = 1; i <= 5; i++) {
      bullets.push(<li key={'bullet-' + i}>{i18n.t('certificates:addNewModal.bullet' + i)}</li>);
    }
    return bullets;
  };

  /**
   * Validate file when selected
   * @param file
   */
  const onFileSelected = async (file: File | null) => {
    if (_.isNil(file)) {
      setFile(null);
      return;
    }

    try {
      const isNameValid = (await acApi.assets.validateByName(file.name, 'CERTIFICATE')).data.result;
      if (isNameValid === true) {
        setFile(file);
        setErrorMessage(null);
      } else {
        //Name is already occupied
        setErrorMessage(i18n.t('certificates:errorMsgs.fileExists'));
      }
    } catch (e) {
      clearFileInput();
    }
  };

  const updatePassword = (password: string) => {
    const updated = _.cloneDeep(passwords);
    updated.keyStore = password;
    updated.ksGranted = false;
    setPasswords(updated);
  };

  const updateAliasPassword = (aliasName: string, password: string) => {
    const updated = _.cloneDeep(aliases);
    const indexToUpdate = _.findIndex(aliases, alias => alias.name === aliasName);
    updated[indexToUpdate].password = btoa(password);
    setAliases(updated);
  };

  const certActionIcons = () => {
    return (
      <div className={'text-right'}>
        <BlazeTableActions>
          <Button className={'btn-link-dark btn-no-hover'}>
            <BzmIcon
              title={i18n.t('common:sslConfig.certificateContent.copy')}
              onClick={() => {
                copyToClipboard(atob(certAsset.data), i18n.t('common:sslConfig.certificateContent.copyDataSuccessMsg'));
              }}
              className="icon-xs text-primary "
              variant={'icon-bzm-clipboard'}
            />
          </Button>
          <Button className={'btn-link-dark btn-no-hover'}>
            <BzmIcon
              title={i18n.t('common:sslConfig.certificateContent.download')}
              variant={'icon-bzm-download'}
              className={'icon-xs text-primary'}
              onClick={() => {
                dispatch(downloadAsset(certAsset));
              }}
            />
          </Button>
        </BlazeTableActions>
      </div>
    );
  };

  const getShowDataContent = () => {
    const marginTop = isViewCert ? 'mt-0' : 'mt-6';
    return (
      <div className={marginTop}>
        <Row>
          <Col md={10}>
            <label htmlFor="keystorePassword">{i18n.t('common:sslConfig.certificateContent.label')}</label>
          </Col>
          <Col md={2} style={{ marginRight: '0px' }}>
            {certActionIcons()}
          </Col>
        </Row>
        <Form.Control
          as="textarea"
          disabled={true}
          id="qa-textarea-cert-content"
          rows={22}
          name="content"
          placeholder={i18n.t('transactions:editor.bodyContentPlaceHolder')}
          value={atob(certAsset.data)}
        />
      </div>
    );
  };
  /** Inputs for storing passwords for JKS stores. */
  const getPasswordInputs = () => {
    return (
      <>
        <div className={'mt-6'}>
          <label htmlFor="keystorePassword">{i18n.t('common:sslConfig.keystorePassword.label')}</label>
          <PasswordInput
            disabled={passwords.ksGranted}
            value={hasPassword(passwords.keyStore) ? passwords.keyStore : ''}
            onChange={val => updatePassword(val)}
            showRevealOption={false}
          />
          <InlineHelp className={'mt-2'}>{i18n.t('common:sslConfig.keystorePassword.hint')}</InlineHelp>
        </div>

        {/* Multiple Aliases */}
        {!_.isNil(aliases) && _.size(aliases) > 0 && (
          <div className={'mt-4 mb-3'}>
            <label htmlFor="" className={'align-middle'}>
              {i18n.t('common:sslConfig.aliases.label')}
            </label>
            <p>{i18n.t('common:sslConfig.aliases.aliasesFound')}</p>
            <AliasesTable aliases={aliases} onUpdateAliasPassword={updateAliasPassword} />
          </div>
        )}

        {!_.isNil(passwordValidationMsg) && (
          <Alert variant={'warning'} className={'mt-4'}>
            <BzmIcon variant={'icon-bzm-caution'} className={'alert-icon'} />
            <p className="alert-message mb-0">{passwordValidationMsg}</p>
          </Alert>
        )}
      </>
    );
  };

  const getButtonText = () => {
    //User is correcting password
    if (!_.isNil(passwordValidationMsg) || !isAliasesListFullGranted(aliases)) {
      return i18n.t('common:sslConfig.updatePassword');
    }

    if (isCertificatedWithValidData()) {
      return i18n.t('transactions:processingActions.modal.close');
    }

    //Keystore is selected, we need another steps (enter passwords)
    if (
      (isEditCert && isKeystoreFile(props.fileName)) ||
      (!isEditCert && !_.isNil(file) && isKeystoreFile(file.name))
    ) {
      return i18n.t('transactions:processingActions.modal.continue');
    }

    return i18n.t('transactions:processingActions.modal.save');
  };

  const isKeyStore = isKeystoreFile(isEditCert ? props.fileName : !_.isNil(file) ? file.name : null);
  const isViewCert = !_.isNil(props.cert) && !isKeystoreFile(props.fileName);

  return (
    <Modal
      show={true}
      enforceFocus={false}
      id="qa-modal-bulk-tag-modal"
      centered={true}
      backdrop={true}
      backdropClassName={'backdrop-faded'}
      onHide={() => props.onClose()}
    >
      <Modal.Header closeButton id="qa-bulk-tagger-modal-btn-close-icon">
        {isViewCert ? (
          <h3 className="modal-title">
            <span className="text-muted">View Certificate: </span>
            <span> {props.fileName}</span>
          </h3>
        ) : (
          <h5 className="modal-title">
            {props.fileName ? 'Edit Certificate' : i18n.t('certificates:addNewModal.title')}
          </h5>
        )}
      </Modal.Header>
      <Modal.Body>
        {!isViewCert && (
          <>
            <p>{i18n.t('certificates:addNewModal.desc')}</p>
            <ul>{getBulletPoints()}</ul>
            <label className={'cursor-pointer mb-1'} htmlFor={'fileUpload'}>
              Certificates
            </label>
            {isEditCert ? (
              <div>
                <Form.Control
                  id={'fileName' + certAsset.id}
                  name="fileName"
                  autoComplete="off"
                  value={props.fileName}
                  disabled={true}
                />
                <p className={'text-muted mt-2 mb-0'}>{i18n.t('certificates:addNewModal.uploadDesc')}</p>
              </div>
            ) : (
              <UploadInput
                removeHandler={() => {
                  clearFileInput();
                  setErrorMessage(null);
                }}
                disabled={!_.isNil(passwordValidationMsg) || !_.isNil(assetId)}
                uploadId={'fileUpload'}
                isInProgress={isUploading}
                description={i18n.t('certificates:addNewModal.uploadDesc')}
                placeholder={i18n.t('certificates:addNewModal.uploadPlaceholder')}
                onFileSelected={onFileSelected}
                supportedExtensions={SUPPORTED_CERT_EXTENSIONS.toString() + ',' + SUPPORTED_KEYSTORES.toString()}
                selectedFileName={!_.isNil(file) ? file.name : undefined}
                hasError={!_.isNil(errorMessage)}
              />
            )}{' '}
          </>
        )}

        {isKeyStore && !errorMessage && getPasswordInputs()}
        {!isKeyStore && !_.isNil(certAsset) && getShowDataContent()}

        {/* Upload Danger msg */}
        {errorMessage && (
          <Alert variant="danger" className={'mt-6'}>
            <BzmIcon variant={'icon-bzm-caution'} className={'alert-icon'} />
            <p className="alert-message mb-0">{errorMessage}</p>
          </Alert>
        )}
        {/*./ Upload Danger msg */}
      </Modal.Body>
      <Modal.Footer>
        {!isCertificatedWithValidData() && (
          <Button id={'cancel-txn-save-btn'} variant="link" onClick={props.onClose}>
            {i18n.t('common:actions.cancel')}
          </Button>
        )}
        <Button
          id={'approve-txn-save-btn'}
          variant="primary"
          onClick={_.isNil(assetId) ? onSaveHandler : onUpdatePasswordHandler}
          disabled={(_.isNil(file) && _.isNil(props.fileName)) || isUploading || isUpdatingPasswords}
        >
          {isUpdatingPasswords && (
            <SimpleLoader loaderSize={18} className={'d-inline-block mr-2'} showLoadingText={false} />
          )}
          {getButtonText()}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default CertificateDetailsModal;
